import { jwtDecode } from 'jwt-decode'
import type { StytchJWTPayload } from '@/types/stytch'

function readCookie(name: string): string {
  if (typeof document === 'undefined') return ''
  return (
    document.cookie
      .split('; ')
      .map((cookiePart) => cookiePart.split('='))
      .find(([cookieName]) => cookieName === name)?.[1]
      ?.trim() ?? ''
  )
}

/**
 * Reads and decodes the Stytch session JWT from browser cookies.
 */
export function getStytchJWT(): StytchJWTPayload | null {
  try {
    const sessionJWT = readCookie('stytch_session_jwt')
    if (!sessionJWT) {
      return null
    }

    return jwtDecode<StytchJWTPayload>(sessionJWT)
  } catch {
    return null
  }
}

/**
 * Reads the browser Stytch session cookies used by `session.updateSession`.
 * The opaque session token cookie is `stytch_session`.
 */
export function getStytchSessionTokens(): { sessionJwt: string; sessionToken: string } | null {
  const sessionJwt = readCookie('stytch_session_jwt')
  const sessionToken = readCookie('stytch_session')
  if (!sessionJwt || !sessionToken) return null
  return { sessionJwt, sessionToken }
}

/**
 * Returns true if the Stytch session JWT's local expiry (`exp`) has passed.
 * Note: Stytch JWTs have a short local cache window (~5 min). An expired JWT
 * does not necessarily mean the server-side session is invalid — the client
 * SDK will obtain a fresh JWT on the next authenticated request.
 * Returns true (expired) when the token is malformed or missing.
 */
export function isSessionJwtExpired(sessionJwt: string): boolean {
  try {
    const { exp } = jwtDecode<StytchJWTPayload>(sessionJwt)
    return Date.now() >= exp * 1000
  } catch {
    return true
  }
}
